import {
  useMutation,
  useQueryClient,
  queryOptions,
  useSuspenseQuery,
  type MutationFunction,
} from '@tanstack/react-query';
import { env } from '@/constants/env';
import type { MutationConfig, QueryConfig } from '@/lib/react-query';
import type { LoaderProps } from '@/lib/router';
import type { Archive } from '@/features/document-archives/core/types';
import { api, extractData } from '@/lib/treaty';

// Get Estate Archives
export async function getEstateArchives(estateId: string): Promise<Archive[]> {
  return extractData(api.estates({ id: estateId })['document-archives'].get());
}

// Get Project Archives
export async function getProjectArchives(projectId: string): Promise<Archive[]> {
  return extractData(api.projects({ id: projectId })['document-archive'].get());
}

export const getEstateArchivesQueryOptions = (estateId: string, _order: 'asc' | 'desc') =>
  queryOptions({
    queryKey: ['archives', `estate:${estateId}`],
    queryFn: () => getEstateArchives(estateId),
  });

export const ensureEstateArchivesQueryData = ({
  context: { queryClient },
  params,
}: LoaderProps) => {
  return queryClient.ensureQueryData(getEstateArchivesQueryOptions(params.estateId, 'desc'));
};

// Create Estate Archive
type CreateEstateArchiveInput = {
  estateId: string;
  name: string;
};

export async function createEstateArchive({
  estateId,
  name,
}: CreateEstateArchiveInput): Promise<Archive> {
  return extractData(api.estates({ id: estateId })['document-archives'].post({ name }));
}

type CreateEstateArchiveFn = MutationFunction<Archive, string>;

export const createEstateArchiveMutationOptions = (estateId: string) => ({
  mutationKey: ['create-archive', estateId],
  mutationFn: (name: string) => createEstateArchive({ estateId, name }),
});

type UseCreateEstateArchiveOptions = {
  mutationConfig?: MutationConfig<CreateEstateArchiveFn>;
};

export function useCreateEstateArchive(
  estateId: string,
  { mutationConfig }: UseCreateEstateArchiveOptions = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...createEstateArchiveMutationOptions(estateId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: ['archives', `estate:${estateId}`],
      });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Create Project Archive
type CreateProjectArchiveInput = {
  projectId: string;
  name: string;
};

export async function createProjectArchive({
  projectId,
  name,
}: CreateProjectArchiveInput): Promise<Archive> {
  return extractData(api.projects({ id: projectId })['document-archives'].post({ name }));
}

type CreateProjectArchiveFn = MutationFunction<Archive, string>;

export const createProjectArchiveMutationOptions = (projectId: string) => ({
  mutationKey: ['create-archive', projectId],
  mutationFn: (name: string) => createProjectArchive({ projectId, name }),
});

type UseCreateProjectArchiveOptions = {
  mutationConfig?: MutationConfig<CreateProjectArchiveFn>;
};

export function useCreateProjectArchive(
  projectId: string,
  { mutationConfig }: UseCreateProjectArchiveOptions = {}
) {
  const queryClient = useQueryClient();

  return useMutation({
    ...createProjectArchiveMutationOptions(projectId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({
        queryKey: ['archives', `project:${projectId}`],
      });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Rename Estate Archive
type RenameEstateArchiveInput = {
  estateId: string;
  archiveId: string;
  name: string;
};

export async function renameEstateArchive({
  estateId,
  archiveId,
  name,
}: RenameEstateArchiveInput): Promise<Archive> {
  return extractData(
    api.estates({ id: estateId })['document-archives']({ archiveId }).put({ name })
  );
}

export const renameEstateArchiveMutationOptions = (estateId: string) => ({
  mutationKey: ['rename-archive', estateId],
  mutationFn: (vars: { archiveId: string; name: string }) =>
    renameEstateArchive({ estateId, archiveId: vars.archiveId, name: vars.name }),
});

type UseRenameEstateArchiveOptions = {
  mutationConfig?: MutationConfig<(vars: { archiveId: string; name: string }) => Promise<Archive>>;
};

export function useRenameEstateArchive(
  estateId: string,
  { mutationConfig }: UseRenameEstateArchiveOptions = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...renameEstateArchiveMutationOptions(estateId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['archives', `estate:${estateId}`] });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Rename Project Archive
type RenameProjectArchiveInput = {
  projectId: string;
  archiveId: string;
  name: string;
};

export async function renameProjectArchive({
  projectId,
  archiveId,
  name,
}: RenameProjectArchiveInput): Promise<Archive> {
  return extractData(
    api.projects({ id: projectId })['document-archives']({ archiveId }).put({ name })
  );
}

export const renameProjectArchiveMutationOptions = (projectId: string) => ({
  mutationKey: ['rename-archive', projectId],
  mutationFn: (vars: { archiveId: string; name: string }) =>
    renameProjectArchive({ projectId, archiveId: vars.archiveId, name: vars.name }),
});

type UseRenameProjectArchiveOptions = {
  mutationConfig?: MutationConfig<(vars: { archiveId: string; name: string }) => Promise<Archive>>;
};

export function useRenameProjectArchive(
  projectId: string,
  { mutationConfig }: UseRenameProjectArchiveOptions = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...renameProjectArchiveMutationOptions(projectId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['archives', `project:${projectId}`] });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Purge (empty) Estate Archive
type PurgeEstateArchiveInput = {
  estateId: string;
  archiveId: string;
};

export async function purgeEstateArchive({
  estateId,
  archiveId,
}: PurgeEstateArchiveInput): Promise<void> {
  await extractData(
    api.estates({ id: estateId })['document-archives']({ archiveId }).purge.post({})
  );
}

type PurgeEstateArchiveFn = MutationFunction<void, string>; // param is archiveId

export const purgeEstateArchiveMutationOptions = (estateId: string) => ({
  mutationKey: ['purge-archive', estateId],
  mutationFn: (archiveId: string) => purgeEstateArchive({ estateId, archiveId }),
});

type UsePurgeEstateArchiveOptions = {
  mutationConfig?: MutationConfig<PurgeEstateArchiveFn>;
};

export function usePurgeEstateArchive(
  estateId: string,
  { mutationConfig }: UsePurgeEstateArchiveOptions = {}
) {
  return useMutation({
    ...purgeEstateArchiveMutationOptions(estateId),
    ...mutationConfig,
  });
}

// Purge (empty) Project Archive
type PurgeProjectArchiveInput = {
  projectId: string;
  archiveId: string;
};

export async function purgeProjectArchive({
  projectId,
  archiveId,
}: PurgeProjectArchiveInput): Promise<void> {
  await extractData(
    api.projects({ id: projectId })['document-archives']({ archiveId }).purge.post({})
  );
}

type PurgeProjectArchiveFn = MutationFunction<void, string>;

export const purgeProjectArchiveMutationOptions = (projectId: string) => ({
  mutationKey: ['purge-archive', projectId],
  mutationFn: (archiveId: string) => purgeProjectArchive({ projectId, archiveId }),
});

type UsePurgeProjectArchiveOptions = {
  mutationConfig?: MutationConfig<PurgeProjectArchiveFn>;
};

export function usePurgeProjectArchive(
  projectId: string,
  { mutationConfig }: UsePurgeProjectArchiveOptions = {}
) {
  return useMutation({
    ...purgeProjectArchiveMutationOptions(projectId),
    ...mutationConfig,
  });
}

// Delete Estate Archive
type DeleteEstateArchiveInput = {
  estateId: string;
  archiveId: string;
};

export async function deleteEstateArchive({
  estateId,
  archiveId,
}: DeleteEstateArchiveInput): Promise<void> {
  await extractData(api.estates({ id: estateId })['document-archives']({ archiveId }).delete());
}

type DeleteEstateArchiveFn = MutationFunction<void, string>; // param is archiveId

export const deleteEstateArchiveMutationOptions = (estateId: string) => ({
  mutationKey: ['delete-archive', estateId],
  mutationFn: (archiveId: string) => deleteEstateArchive({ estateId, archiveId }),
});

type UseDeleteEstateArchiveOptions = {
  mutationConfig?: MutationConfig<DeleteEstateArchiveFn>;
};

export function useDeleteEstateArchive(
  estateId: string,
  { mutationConfig }: UseDeleteEstateArchiveOptions = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...deleteEstateArchiveMutationOptions(estateId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['archives', `estate:${estateId}`] });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Delete Project Archive
type DeleteProjectArchiveInput = {
  projectId: string;
  archiveId: string;
};

export async function deleteProjectArchive({
  projectId,
  archiveId,
}: DeleteProjectArchiveInput): Promise<void> {
  await extractData(api.projects({ id: projectId })['document-archives']({ archiveId }).delete());
}

type DeleteProjectArchiveFn = MutationFunction<void, string>;

export const deleteProjectArchiveMutationOptions = (projectId: string) => ({
  mutationKey: ['delete-archive', projectId],
  mutationFn: (archiveId: string) => deleteProjectArchive({ projectId, archiveId }),
});

type UseDeleteProjectArchiveOptions = {
  mutationConfig?: MutationConfig<DeleteProjectArchiveFn>;
};

export function useDeleteProjectArchive(
  projectId: string,
  { mutationConfig }: UseDeleteProjectArchiveOptions = {}
) {
  const queryClient = useQueryClient();
  return useMutation({
    ...deleteProjectArchiveMutationOptions(projectId),
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      await queryClient.invalidateQueries({ queryKey: ['archives', `project:${projectId}`] });
      mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
}

// Estate archive by id (with rootFolder)
export async function getEstateArchiveById(
  estateId: string,
  archiveId: string
): Promise<Archive & { rootFolder?: unknown }> {
  return extractData(
    api.estates({ id: estateId })['document-archives']({ archiveId }).get()
  ) as Promise<Archive & { rootFolder?: unknown }>;
}

export const getEstateArchiveByIdQueryOptions = (estateId: string, archiveId: string) =>
  queryOptions({
    queryKey: ['estate-archive', estateId, archiveId] as const,
    queryFn: () => getEstateArchiveById(estateId, archiveId),
  });

export function useEstateArchiveById({
  estateId,
  archiveId,
  queryConfig,
}: {
  estateId: string;
  archiveId: string;
  queryConfig?: QueryConfig<typeof getEstateArchiveById>;
}) {
  return useSuspenseQuery({
    ...queryConfig,
    ...getEstateArchiveByIdQueryOptions(estateId, archiveId),
  });
}

// Project archive by id (with rootFolder)
export async function getProjectArchiveById(
  projectId: string,
  archiveId: string
): Promise<Archive & { rootFolder?: unknown }> {
  return extractData(
    api.projects({ id: projectId })['document-archive']({ archiveId }).get()
  ) as Promise<Archive & { rootFolder?: unknown }>;
}

export const getProjectArchiveByIdQueryOptions = (projectId: string, archiveId: string) =>
  queryOptions({
    queryKey: ['project-archive', projectId, archiveId] as const,
    queryFn: () => getProjectArchiveById(projectId, archiveId),
  });

export function useProjectArchiveById({
  projectId,
  archiveId,
  queryConfig,
}: {
  projectId: string;
  archiveId: string;
  queryConfig?: QueryConfig<typeof getProjectArchiveById>;
}) {
  return useSuspenseQuery({
    ...queryConfig,
    ...getProjectArchiveByIdQueryOptions(projectId, archiveId),
  });
}

// Share Estate archive
type CreateEstateFolderShareInput = {
  estateId: string;
  folderId: string;
  expiresIn?: number;
  parts?: string[];
};

export type SharedFolderToken = {
  token: string;
  folderId: string | null;
  expiryDate: string;
  createdAt: string;
  additionalData?: string | null;
};

export async function createEstateFolderShare({
  estateId,
  folderId,
  expiresIn = 730, // default ~2 years
  parts = [],
}: CreateEstateFolderShareInput): Promise<{ token: string; folderId: string }> {
  return extractData(
    api.estates({ id: estateId }).folders({ folderId }).share.post({ expiresIn, parts })
  );
}

type CreateEstateFolderShareFn = MutationFunction<
  { token: string; folderId: string },
  { folderId: string; expiresIn?: number; parts?: string[] }
>;

export const createEstateFolderShareMutationOptions = (estateId: string) => ({
  mutationKey: ['share-folder', estateId],
  mutationFn: (vars: { folderId: string; expiresIn?: number; parts?: string[] }) =>
    createEstateFolderShare({
      estateId,
      folderId: vars.folderId,
      expiresIn: vars.expiresIn,
      parts: vars.parts,
    }),
});

export function useShareEstateFolder(
  estateId: string,
  { mutationConfig }: { mutationConfig?: MutationConfig<CreateEstateFolderShareFn> } = {}
) {
  return useMutation({
    ...createEstateFolderShareMutationOptions(estateId),
    ...mutationConfig,
  });
}

export async function getEstateFolderShareLinks(
  estateId: string,
  folderId: string
): Promise<SharedFolderToken[]> {
  return extractData(api.estates({ id: estateId }).folders({ folderId }).links.get()) as Promise<
    SharedFolderToken[]
  >;
}

export async function revokeEstateFolderShareLink(
  estateId: string,
  folderId: string,
  token: string
): Promise<void> {
  await extractData(
    api.estates({ id: estateId }).folders({ folderId }).links['revoke'].post({ token })
  );
}

type RevokeEstateFolderShareLinkFn = MutationFunction<void, { folderId: string; token: string }>;

export const revokeEstateFolderShareLinkMutationOptions = (estateId: string) => ({
  mutationKey: ['revoke-folder-link', estateId],
  mutationFn: (vars: { folderId: string; token: string }) =>
    revokeEstateFolderShareLink(estateId, vars.folderId, vars.token),
});

export function useRevokeEstateFolderShareLink(
  estateId: string,
  { mutationConfig }: { mutationConfig?: MutationConfig<RevokeEstateFolderShareLinkFn> } = {}
) {
  return useMutation({
    ...revokeEstateFolderShareLinkMutationOptions(estateId),
    ...mutationConfig,
  });
}

export const getEstateFolderShareLinksQueryOptions = (estateId: string, folderId: string) =>
  queryOptions({
    queryKey: ['estate-folder-links', estateId, folderId] as const,
    queryFn: () => getEstateFolderShareLinks(estateId, folderId),
  });

// Project folder sharing
type CreateProjectFolderShareInput = {
  projectId: string;
  folderId: string;
  expiresIn?: number;
  parts?: string[];
};

export async function createProjectFolderShare({
  projectId,
  folderId,
  expiresIn = 730,
  parts = [],
}: CreateProjectFolderShareInput): Promise<{ token: string; folderId: string }> {
  return extractData(
    api.projects({ id: projectId }).folders({ folderId }).share.post({ expiresIn, parts })
  );
}

type CreateProjectFolderShareFn = MutationFunction<
  { token: string; folderId: string },
  { folderId: string; expiresIn?: number; parts?: string[] }
>;

export const createProjectFolderShareMutationOptions = (projectId: string) => ({
  mutationKey: ['share-folder', projectId],
  mutationFn: (vars: { folderId: string; expiresIn?: number; parts?: string[] }) =>
    createProjectFolderShare({
      projectId,
      folderId: vars.folderId,
      expiresIn: vars.expiresIn,
      parts: vars.parts,
    }),
});

export function useShareProjectFolder(
  projectId: string,
  { mutationConfig }: { mutationConfig?: MutationConfig<CreateProjectFolderShareFn> } = {}
) {
  return useMutation({
    ...createProjectFolderShareMutationOptions(projectId),
    ...mutationConfig,
  });
}

export async function getProjectFolderShareLinks(
  projectId: string,
  folderId: string
): Promise<SharedFolderToken[]> {
  return extractData(api.projects({ id: projectId }).folders({ folderId }).links.get()) as Promise<
    SharedFolderToken[]
  >;
}

export async function revokeProjectFolderShareLink(
  projectId: string,
  folderId: string,
  token: string
): Promise<void> {
  await extractData(
    api.projects({ id: projectId }).folders({ folderId }).links['revoke'].post({ token })
  );
}

type RevokeProjectFolderShareLinkFn = MutationFunction<void, { folderId: string; token: string }>;

export const revokeProjectFolderShareLinkMutationOptions = (projectId: string) => ({
  mutationKey: ['revoke-folder-link', projectId],
  mutationFn: (vars: { folderId: string; token: string }) =>
    revokeProjectFolderShareLink(projectId, vars.folderId, vars.token),
});

export function useRevokeProjectFolderShareLink(
  projectId: string,
  { mutationConfig }: { mutationConfig?: MutationConfig<RevokeProjectFolderShareLinkFn> } = {}
) {
  return useMutation({
    ...revokeProjectFolderShareLinkMutationOptions(projectId),
    ...mutationConfig,
  });
}

export const getProjectFolderShareLinksQueryOptions = (projectId: string, folderId: string) =>
  queryOptions({
    queryKey: ['project-folder-links', projectId, folderId] as const,
    queryFn: () => getProjectFolderShareLinks(projectId, folderId),
  });

export function useEstateFolderShareLinks({
  estateId,
  folderId,
  queryConfig,
}: {
  estateId: string;
  folderId: string;
  queryConfig?: QueryConfig<typeof getEstateFolderShareLinks>;
}) {
  return useSuspenseQuery({
    ...queryConfig,
    ...getEstateFolderShareLinksQueryOptions(estateId, folderId),
  });
}

export function useProjectFolderShareLinks({
  projectId,
  folderId,
  queryConfig,
}: {
  projectId: string;
  folderId: string;
  queryConfig?: QueryConfig<typeof getProjectFolderShareLinks>;
}) {
  return useSuspenseQuery({
    ...queryConfig,
    ...getProjectFolderShareLinksQueryOptions(projectId, folderId),
  });
}

export async function downloadEstateDocumentsZip(
  estateId: string,
  documentIds: string[]
): Promise<void> {
  const url = `${env.API_URL}/estates/${encodeURIComponent(estateId)}/documents/download`;

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*' },
    body: JSON.stringify({ documentIds }),
  });

  if (!res.ok) throw new Error('Download failed');

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const disposition =
    res.headers.get('Content-Disposition') ?? res.headers.get('content-disposition');
  let filename = 'documents.zip';
  const match = disposition && /filename="(.+?)"/i.exec(disposition);
  if (match?.[1]) {
    filename = match[1];
  }

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

export async function downloadEstateDocument(estateId: string, documentId: string): Promise<void> {
  return downloadEstateDocumentsZip(estateId, [documentId]);
}

export async function downloadProjectDocumentsZip(
  projectId: string,
  documentIds: string[]
): Promise<void> {
  const url = `${env.API_URL}/projects/${encodeURIComponent(projectId)}/documents/download`;

  const res = await fetch(url, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', Accept: '*/*' },
    body: JSON.stringify({ documentIds }),
  });

  if (!res.ok) throw new Error('Download failed');

  const blob = await res.blob();
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');

  const disposition =
    res.headers.get('Content-Disposition') ?? res.headers.get('content-disposition');
  let filename = 'documents.zip';
  const match = disposition && /filename="(.+?)"/i.exec(disposition);
  if (match?.[1]) {
    filename = match[1];
  }

  link.href = objectUrl;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(objectUrl);
}

export async function downloadProjectDocument(
  projectId: string,
  documentId: string
): Promise<void> {
  return downloadProjectDocumentsZip(projectId, [documentId]);
}
